import React from 'react';
import { Card, CardContent, Typography, Box, List, ListItem, ListItemText, Divider } from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { BusinessConfig } from '../../types';
import { dayjs } from '../../utils/dayjs-config';

interface BusinessHoursCardProps {
  hours?: BusinessConfig['businessInfo']['businessHours'];
  title?: string;
  hideHeader?: boolean;
}

const dayOrder: Array<keyof BusinessConfig['businessInfo']['businessHours']> = [
  'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'
];

const labelFor = (day: string) => day.charAt(0).toUpperCase() + day.slice(1);

const formatRange = (range: string) => {
  if (!range) return 'Closed';
  const trimmed = range.trim();
  if (/^closed$/i.test(trimmed)) return 'Closed';
  if (/am|pm/i.test(trimmed)) {
    return trimmed.replace(/\s*-\s*/g, ' – ');
  }
  const m = trimmed.match(/^(\d{4})-(\d{4})$/);
  if (m) {
    try {
      const { convertTo12Hour } = require('../../time/timeUtils');
      return `${convertTo12Hour(m[1])} – ${convertTo12Hour(m[2])}`;
    } catch {
      return trimmed;
    }
  }
  return trimmed;
};

const BusinessHoursCard: React.FC<BusinessHoursCardProps> = ({ hours, title = 'Business Hours', hideHeader = false }) => {
  const fallback: BusinessConfig['businessInfo']['businessHours'] = {
    monday: 'Closed',
    tuesday: 'Closed',
    wednesday: 'Closed',
    thursday: 'Closed',
    friday: 'Closed',
    saturday: 'Closed',
    sunday: 'Closed',
  };
  const safeHours = (hours ?? fallback) as BusinessConfig['businessInfo']['businessHours'];
  const today = dayjs.tz().format('dddd').toLowerCase();
  return (
    <Card sx={{ maxWidth: 560, mx: 'auto', borderRadius: 2, boxShadow: 3 }}>
      <CardContent>
        {!hideHeader && (
          <>
            <Box display="flex" alignItems="center" justifyContent="center" gap={1} mb={1}>
              <AccessTimeIcon color="secondary" />
              <Typography variant="h6" sx={{ fontWeight: 700 }}>{title}</Typography>
            </Box>
            <Divider sx={{ mb: 1 }} />
          </>
        )}
        <List dense disablePadding>
          {dayOrder.map((day, idx) => {
            const isToday = day === today;
            return (
              <React.Fragment key={day}>
                <ListItem
                  sx={{
                    py: 0.75,
                    pr: 0,
                    px: 0,
                    bgcolor: 'transparent',
                  }}
                >
                  <ListItemText
                    primary={
                      <Box display="flex" justifyContent="space-between" alignItems="center" width="100%" gap={2}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography
                            variant="body2"
                          sx={{
                            fontWeight: isToday ? 700 : 600,
                            color: isToday ? 'secondary.main' : 'text.primary',
                          }}
                          >
                            {labelFor(day)}
                          </Typography>
                        </Box>
                        <Typography
                          variant="body2"
                          sx={{
                            color: isToday ? 'secondary.main' : 'text.secondary',
                            fontWeight: isToday ? 600 : 400,
                            textAlign: 'right',
                          }}
                        >
                          {formatRange((safeHours as any)[day])}
                        </Typography>
                      </Box>
                    }
                  />
                </ListItem>
                {idx < dayOrder.length - 1 && <Divider component="li" sx={{ opacity: 0.3 }} />}
              </React.Fragment>
            );
          })}
        </List>
      </CardContent>
    </Card>
  );
};

export default BusinessHoursCard;
